import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Layout from "@/components/Layout";
import ProductsIntro from "@/components/ProductsIntro";
import { api } from "@/lib/api";

function money(cents) {
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export default function Products() {
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setGroups(r.data.products || [])).catch(() => {});
  }, []);

  return (
    <Layout>
      <ProductsIntro />
      {/* Hero */}
      <section
        data-testid="products-hero"
        className="relative pt-24 pb-16 sm:pt-32 sm:pb-20 border-b border-white/[0.05]"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1200px] h-[600px] opacity-[0.08] blur-3xl"
               style={{ background: "radial-gradient(circle at 50% 0%,#ff5a00,transparent 60%)" }} />
        </div>
        <div className="relative z-10 max-w-[1200px] mx-auto px-6 sm:px-10 text-center">
          <p className="font-mono text-[11px] uppercase tracking-[0.45em] text-[#ff5a00]">The Studio · Products</p>
          <h1
            data-testid="products-headline"
            className="font-display uppercase leading-[0.92] mt-6 text-[#ededed]"
            style={{ fontSize: "clamp(2.75rem, 8vw, 6rem)", letterSpacing: "-0.005em" }}
          >
            From a one-line pitch
            <br />
            <span className="italic text-[#ff5a00]">to a finished feature.</span>
          </h1>
          <p className="font-display italic text-lg sm:text-2xl text-[#a1a1aa] mt-8 max-w-3xl mx-auto leading-relaxed">
            Five products. One ladder. Every rung priced honestly, delivered on a
            timeline no traditional studio can match.
          </p>
        </div>
      </section>

      {/* Ladder */}
      <section className="max-w-[1200px] mx-auto px-6 sm:px-10 py-20">
        <div className="grid gap-6">
          {groups.map((g, i) => (
            <motion.div
              key={g.slug}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, delay: i * 0.05 }}
              data-testid={`product-card-${g.slug}`}
              className="group relative border border-[#27272a] bg-[#0b0b0d]/60 backdrop-blur hover:border-[#ff5a00]/60 transition-colors"
            >
              <Link
                to={`/products/${g.slug}`}
                className="grid grid-cols-1 md:grid-cols-[110px_1fr_auto] items-start gap-6 md:gap-10 px-6 sm:px-10 py-8 sm:py-10"
              >
                <span className="font-mono text-[11px] tracking-[0.32em] text-[#52525b] group-hover:text-[#ff5a00] transition-colors">
                  {String(g.order).padStart(2, "0")}
                </span>
                <div>
                  <h3 className="font-display uppercase text-[#ededed] leading-[0.95] group-hover:text-[#ff5a00] transition-colors"
                      style={{ fontSize: "clamp(1.8rem, 4vw, 3rem)" }}>
                    {g.name}
                  </h3>
                  <p className="font-display italic text-base sm:text-lg text-[#a1a1aa] mt-3 max-w-2xl">
                    {g.tagline}
                  </p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#52525b] mt-4">
                    {g.items.length} item{g.items.length > 1 ? "s" : ""} · starting {money(Math.min(...g.items.map((it) => it.amount_cents)))}
                  </p>
                </div>
                <div className="flex items-center gap-3 md:pt-3">
                  <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#ff5a00]">
                    Explore →
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {!groups.length && (
          <p className="text-center font-mono text-xs uppercase tracking-[0.28em] text-[#52525b] py-24">
            The forge is warming — check back in a moment.
          </p>
        )}
      </section>
    </Layout>
  );
}
